import { useCallback, useEffect, useState } from "react";
import { useIndexedDBContext } from "../providers/indexed-db-provider";

export interface LogEntry {
    id: string;
    type: "error" | "warning" | "info";
    message: string;
    stack?: string;
    timestamp: number;
}

export const useLogMonitoring = () => {
    const { logsDb } = useIndexedDBContext();
    const [logs, setLogs] = useState<LogEntry[]>([]);

    const loadLogs = useCallback(async () => {
        if (!logsDb) return;
        const items = await logsDb.getAllItems();
        setLogs(items.sort((a: LogEntry, b: LogEntry) => b.timestamp - a.timestamp));
    }, [logsDb]);

    const addLog = useCallback(
        async (log: Omit<LogEntry, "id" | "timestamp">) => {
            if (!logsDb) return;
            const newLog: LogEntry = {
                ...log,
                id: crypto.randomUUID(),
                timestamp: Date.now(),
            };

            await logsDb.addItem(newLog);
            await loadLogs();
        },
        [logsDb, loadLogs]
    );

    const clearLogs = useCallback(async () => {
        if (!logsDb) return;
        await logsDb.deleteItem("all");
        setLogs([]);
    }, [logsDb]);

    useEffect(() => {
        if (logsDb) {
            loadLogs();
        }
    }, [logsDb, loadLogs]);

    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            addLog({
                type: "error",
                message: event.message,
                stack: event.error?.stack,
            });
        };

        const handleRejection = (event: PromiseRejectionEvent) => {
            addLog({
                type: "error",
                message: event.reason?.message || "Unhandled Promise Rejection",
                stack: event.reason?.stack,
            });
        };

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleRejection);

        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener("unhandledrejection", handleRejection);
        };
    }, [addLog]);

    return {
        logs,
        addLog,
        clearLogs,
    };
};
